import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllNomenclatures } from '../../../hooks/useNomenclatures';
import { getProtocolTabLabelKey } from '../../../utils/translateNomenclature';
import ProtocolCategoryList from './ProtocolCategoryList';
import ProtocolTypeList from './ProtocolTypeList';
import RoomTypeList from './RoomTypeList';
import PPETypeList from './PPETypeList';
import VerificationMethodList from './VerificationMethodList';
import TriggerEventList from './TriggerEventList';
import FrequencyTypeList from './FrequencyTypeList';
import LocationTypeList from './LocationTypeList';
import ActionTypeList from './ActionTypeList';
import EquipmentList from './EquipmentTypeList';
import SupplyList from './SupplyTypeList';
import ConditionTypeList from './ConditionTypeList';
import DocumentationFieldTypeList from './DocumentationFieldTypeList';
import ProcessCategoryList from './ProcessCategoryList';
import InitializeButton from './InitializeButton';
import './NomenclatureManager.css';

/**
 * Nomenclature Manager Component
 * Manages all reference data for the Protocol system
 */
const NomenclatureManager = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('protocol-categories');
  const {
    protocolCategories,
    protocolTypes,
    roomTypes,
    ppeTypes,
    verificationMethods,
    triggerEvents,
    frequencyTypes,
    locationTypes,
    actionTypes,
    equipment,
    supplies,
    conditionTypes,
    documentationFieldTypes,
    processCategories,
    loading,
    error,
    refresh
  } = useAllNomenclatures(false); // Include inactive items for management

  const tabs = [
    { id: 'protocol-categories', label: 'Protocol Categories', count: protocolCategories.length },
    { id: 'protocol-types', label: 'Protocol Types', count: protocolTypes.length },
    { id: 'room-types', label: 'Room Types', count: roomTypes.length },
    { id: 'ppe-types', label: 'PPE Types', count: ppeTypes.length },
    { id: 'verification-methods', label: 'Verification Methods', count: verificationMethods.length },
    { id: 'trigger-events', label: 'Trigger Events', count: triggerEvents.length },
    { id: 'frequency-types', label: 'Frequency Types', count: frequencyTypes.length },
    { id: 'location-types', label: 'Location Types', count: locationTypes.length },
    { id: 'action-types', label: 'Action Types', count: actionTypes.length },
    { id: 'equipment', label: 'Equipment', count: equipment.length },
    { id: 'supplies', label: 'Supplies', count: supplies.length },
    { id: 'condition-types', label: 'Condition Types', count: conditionTypes.length },
    { id: 'documentation-field-types', label: 'Documentation Field Types', count: documentationFieldTypes.length },
    { id: 'process-categories', label: 'Process Categories', count: processCategories.length }
  ];

  const renderContent = () => {
    if (loading) {
      return (
        <div className="nomenclature-loading">
          <div className="spinner"></div>
          <p>Loading nomenclatures...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="nomenclature-error">
          <div className="error-icon">⚠</div>
          <h3>Error Loading Nomenclatures</h3>
          <p>{error.message}</p>
          <button onClick={refresh} className="btn btn-secondary">Retry</button>
        </div>
      );
    }

    switch (activeTab) {
      case 'protocol-categories':
        return <ProtocolCategoryList items={protocolCategories} onUpdate={refresh} />;
      case 'protocol-types':
        return <ProtocolTypeList items={protocolTypes} onUpdate={refresh} />;
      case 'room-types':
        return <RoomTypeList items={roomTypes} onUpdate={refresh} />;
      case 'ppe-types':
        return <PPETypeList items={ppeTypes} onUpdate={refresh} />;
      case 'verification-methods':
        return <VerificationMethodList items={verificationMethods} onUpdate={refresh} />;
      case 'trigger-events':
        return <TriggerEventList items={triggerEvents} onUpdate={refresh} />;
      case 'frequency-types':
        return <FrequencyTypeList items={frequencyTypes} onUpdate={refresh} />;
      case 'location-types':
        return <LocationTypeList items={locationTypes} onUpdate={refresh} />;
      case 'action-types':
        return <ActionTypeList items={actionTypes} onUpdate={refresh} />;
      case 'equipment':
        return <EquipmentList items={equipment} onUpdate={refresh} />;
      case 'supplies':
        return <SupplyList items={supplies} onUpdate={refresh} />;
      case 'condition-types':
        return <ConditionTypeList items={conditionTypes} onUpdate={refresh} />;
      case 'documentation-field-types':
        return <DocumentationFieldTypeList items={documentationFieldTypes} onUpdate={refresh} />;
      case 'process-categories':
        return <ProcessCategoryList items={processCategories} onUpdate={refresh} />;
      default:
        return null;
    }
  };

  const hasNoData = 
    protocolCategories.length === 0 &&
    protocolTypes.length === 0 &&
    roomTypes.length === 0 &&
    ppeTypes.length === 0 &&
    verificationMethods.length === 0 &&
    triggerEvents.length === 0 &&
    frequencyTypes.length === 0 &&
    locationTypes.length === 0 &&
    actionTypes.length === 0 &&
    equipment.length === 0 &&
    supplies.length === 0 &&
    conditionTypes.length === 0 &&
    documentationFieldTypes.length === 0 &&
    processCategories.length === 0;

  return (
    <div className="nomenclature-manager">
      <div className="nomenclature-header">
        <div>
          <h1>Protocol Nomenclatures</h1>
          <p className="subtitle">Manage reference data for the Protocol System</p>
        </div>
        {/* Always show initialize button */}
        <InitializeButton onComplete={refresh} />
      </div>

      <div className="nomenclature-layout">
        {/* Sidebar */}
        <div className="nomenclature-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`nomenclature-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-label">{t(getProtocolTabLabelKey(tab.id))}</span>
              <span className="tab-count">({tab.count})</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="nomenclature-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default NomenclatureManager;

